import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import api from '../api/api';
import { useUser } from '../context/UserContext';
import { useLocation } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import './cleanerDashboard.css';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const toLocalDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const startOfWeek = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
const timeToDate = (time) => {
    const [hours, minutes] = String(time || '08:00').split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
};
const dateToTime = (date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
const dateLabel = (value) => {
    const parsed = new Date(`${String(value || '').slice(0, 10)}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? String(value || '') : parsed.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};
const money = (value) => value === null || value === undefined ? 'Rate not set' : `$${Number(value).toFixed(2)}`;

const CleanerDashboard = () => {
    const { user } = useUser();
    const location = useLocation();
    const [tab, setTab] = useState('jobs');
    const [calendarWeekStart, setCalendarWeekStart] = useState(() => startOfWeek(new Date()));
    const [jobs, setJobs] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [days, setDays] = useState(WEEKDAYS.map((_, dayOfWeek) => ({ dayOfWeek, isAvailable: true, startTime: '08:00', endTime: '18:00' })));
    const [blocks, setBlocks] = useState([]);
    const [blockDate, setBlockDate] = useState('');
    const [blockNote, setBlockNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [jobsRes, availabilityRes, notificationsRes] = await Promise.all([
                api.get('/cleaner-workforce/jobs/me'),
                api.get('/cleaner-workforce/availability/me'),
                api.get('/cleaner-workforce/notifications/me')
            ]);
            setJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
            setDays(Array.isArray(availabilityRes.data?.days) ? availabilityRes.data.days : WEEKDAYS.map((_, dayOfWeek) => ({ dayOfWeek, isAvailable: true, startTime: '08:00', endTime: '18:00' })));
            setBlocks(Array.isArray(availabilityRes.data?.blocks) ? availabilityRes.data.blocks : []);
            setNotifications(Array.isArray(notificationsRes.data) ? notificationsRes.data : []);
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to load cleaner dashboard.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadDashboard(); }, [loadDashboard]);

    useEffect(() => {
        const jobId = new URLSearchParams(location.search).get('jobId');
        if (!jobId || loading) return;
        setTab('jobs');
        const target = document.getElementById(`cleaner-job-${jobId}`);
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target.classList.add('cleaner-job-highlight');
        }
    }, [location.search, loading, jobs]);

    const respondToOffer = async (job, decision, notification = null) => {
        setSaving(`respond-${job.id}`);
        setError('');
        try {
            await api.put(`/cleaner-workforce/jobs/${job.id}/respond`, { decision });
            setNotice(decision === 'accept' ? 'Cleaning accepted.' : 'Offer declined.');
            if (notification && !notification.readAt) await markRead(notification);
            await loadDashboard();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to respond to this offer.');
        } finally { setSaving(''); }
    };

    const takeJob = async (job) => {
        setSaving(`job-${job.id}`);
        setError('');
        try {
            if (job.canClaim) {
                await api.post(`/cleaner-workforce/jobs/${job.id}/claim`, {});
                setNotice('Cleaning claimed. The host has been notified.');
            } else {
                await api.post(`/cleaner-workforce/jobs/${job.id}/request`, {});
                setNotice('Request sent to the host.');
            }
            await loadDashboard();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to claim this cleaning.');
        } finally { setSaving(''); }
    };

    const completeJob = async (job) => {
        setSaving(`complete-${job.id}`);
        setError('');
        try {
            await api.put(`/cleaner-workforce/jobs/${job.id}/complete`, {});
            setNotice('Cleaning marked complete. The host was notified.');
            await loadDashboard();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to complete this cleaning.');
        } finally { setSaving(''); }
    };

    const saveAvailability = async () => {
        setSaving('availability');
        setError('');
        try {
            await api.put('/cleaner-workforce/availability/me', { days });
            setNotice('Weekly availability saved.');
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to save availability.');
        } finally { setSaving(''); }
    };

    const addBlock = async (event) => {
        event.preventDefault();
        setSaving('block');
        setError('');
        try {
            await api.post('/cleaner-workforce/availability/me/blocks', { date: blockDate, note: blockNote });
            setBlockDate('');
            setBlockNote('');
            await loadDashboard();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to block this date.');
        } finally { setSaving(''); }
    };

    const removeBlock = async (block) => {
        setSaving(`block-${block.id}`);
        try {
            await api.delete(`/cleaner-workforce/availability/me/blocks/${block.id}`);
            await loadDashboard();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to remove blocked date.');
        } finally { setSaving(''); }
    };

    const markRead = async (notification) => {
        if (notification.readAt) return;
        try {
            const response = await api.put(`/cleaner-workforce/notifications/${notification.id}/read`, {});
            setNotifications((current) => current.map((item) => item.id === notification.id ? response.data : item));
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to update alert.');
        }
    };

    const todayKey = toLocalDateKey(new Date());
    const upcomingJobs = jobs.filter((job) => String(job.checkoutDate || '').slice(0, 10) >= todayKey);
    const openJobsForDisplay = upcomingJobs.filter((job) => job.status === 'open');
    const assignedJobs = upcomingJobs.filter((job) => job.isAssignedToMe);
    const offers = jobs.filter((job) => job.isOfferToMe);
    const unreadCount = notifications.filter((notification) => !notification.readAt).length;
    const calendarDays = useMemo(() => {
        return Array.from({ length: 42 }, (_, index) => {
            const date = new Date(calendarWeekStart.getFullYear(), calendarWeekStart.getMonth(), calendarWeekStart.getDate() + index);
            return { key: toLocalDateKey(date), date };
        });
    }, [calendarWeekStart]);
    const jobsByCheckoutDate = useMemo(() => {
        const grouped = new Map();
        jobs.forEach((job) => {
            const key = String(job.checkoutDate || '').slice(0, 10);
            const group = grouped.get(key) || [];
            group.push(job);
            grouped.set(key, group);
        });
        return grouped;
    }, [jobs]);
    const moveCalendarWeek = (offset) => setCalendarWeekStart((current) => new Date(current.getFullYear(), current.getMonth(), current.getDate() + offset * 7));
    const calendarRange = `${calendarDays[0].date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${calendarDays[41].date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;

    return (
        <main className="cleaner-dashboard">
            <header className="cleaner-dashboard-header">
                <div><p className="cleaner-dashboard-kicker">Cleaner workspace</p><h1>Hello, {user?.display || 'Cleaner'}</h1><p>Your assigned cleanings, open work, and availability.</p></div>
                <span className="cleaner-dashboard-count">{offers.length} offer{offers.length === 1 ? '' : 's'} awaiting response</span>
            </header>

            <nav className="cleaner-dashboard-tabs" aria-label="Cleaner dashboard sections">
                <button type="button" aria-pressed={tab === 'jobs'} onClick={() => setTab('jobs')}>Cleanings <span>{assignedJobs.length + openJobsForDisplay.length}</span></button>
                <button type="button" aria-pressed={tab === 'calendar'} onClick={() => setTab('calendar')}>Calendar</button>
                <button type="button" aria-pressed={tab === 'availability'} onClick={() => setTab('availability')}>Availability</button>
                <button type="button" aria-pressed={tab === 'alerts'} onClick={() => setTab('alerts')}>Alerts <span>{unreadCount}</span></button>
            </nav>

            {error && <div className="cleaner-dashboard-alert cleaner-dashboard-alert-error" role="alert">{error}</div>}
            {notice && <div className="cleaner-dashboard-alert cleaner-dashboard-alert-success" role="status">{notice}</div>}

            {tab === 'jobs' && (
                <section className="cleaner-jobs-view">
                    {loading ? <p className="cleaner-dashboard-empty">Loading cleanings...</p> : upcomingJobs.length === 0 ? <p className="cleaner-dashboard-empty">No upcoming cleanings are available.</p> : upcomingJobs.map((job) => (
                        <article className={`cleaner-job-item cleaner-job-item-${job.isOfferToMe ? 'offered' : job.status}`} id={`cleaner-job-${job.id}`} key={job.id}>
                            <div className="cleaner-job-item-main">
                                <div className="cleaner-job-item-top"><strong>{job.property?.name || 'Property'}</strong><span className={`cleaner-job-state cleaner-job-state-${job.isOfferToMe ? 'offered' : job.status}`}>{job.isOfferToMe ? 'Offer' : job.status}</span></div>
                                <p>{job.property?.address1}{job.property?.city ? ` · ${job.property.city}` : ''}{job.property?.state ? `, ${job.property.state}` : ''}</p>
                                <div className="cleaner-job-meta"><span>{dateLabel(job.checkoutDate)}</span><span>{job.property?.bedrooms || 0} bed / {job.property?.bathrooms || 0} bath</span><strong>{money(job.agreedRate ?? job.suggestedRate)}</strong></div>
                                {job.hasConflict && <p className="cleaner-job-conflict">This date conflicts with another assignment.</p>}
                            </div>
                            <div className="cleaner-job-actions">
                                {job.isOfferToMe && <>
                                    <button type="button" className="cleaner-button cleaner-button-primary" disabled={saving === `respond-${job.id}`} onClick={() => respondToOffer(job, 'accept')}>Accept</button>
                                    <button type="button" className="cleaner-button cleaner-button-secondary" disabled={saving === `respond-${job.id}`} onClick={() => respondToOffer(job, 'decline')}>Decline</button>
                                </>}
                                {job.status === 'open' && !job.isOfferToMe && <button type="button" className="cleaner-button cleaner-button-primary" disabled={(!job.canClaim && !job.canRequest) || saving === `job-${job.id}`} onClick={() => takeJob(job)}>{saving === `job-${job.id}` ? 'Sending…' : job.hasRequested ? 'Request pending' : job.canClaim ? 'Claim cleaning' : job.canRequest ? 'Request cleaning' : 'Unavailable'}</button>}
                                {job.status === 'accepted' && <button type="button" className="cleaner-button cleaner-button-primary" disabled={saving === `complete-${job.id}`} onClick={() => completeJob(job)}>Mark complete</button>}
                            </div>
                        </article>
                    ))}
                </section>
            )}

            {tab === 'calendar' && (
                <section className="cleaner-calendar-view" aria-label="Available cleanings calendar">
                    <header className="cleaner-calendar-toolbar">
                        <div><p className="cleaner-dashboard-kicker">Checkout schedule</p><h2>{calendarRange}</h2><p className="cleaner-calendar-range-caption">Six weeks, starting with the current week</p></div>
                        <div className="cleaner-calendar-nav">
                            <button type="button" className="cleaner-calendar-nav-button" onClick={() => moveCalendarWeek(-1)} aria-label="Previous week">‹</button>
                            <button type="button" className="cleaner-calendar-today" onClick={() => setCalendarWeekStart(startOfWeek(new Date()))}>This week</button>
                            <button type="button" className="cleaner-calendar-nav-button" onClick={() => moveCalendarWeek(1)} aria-label="Next week">›</button>
                        </div>
                    </header>
                    {loading ? <p className="cleaner-dashboard-empty">Loading calendar...</p> : (
                        <div className="cleaner-calendar-grid">
                            {WEEKDAYS.map((weekday) => <div className="cleaner-calendar-weekday" key={weekday}>{weekday.slice(0, 3)}</div>)}
                            {calendarDays.map((day) => {
                                const dayJobs = jobsByCheckoutDate.get(day.key) || [];
                                return <div className={`cleaner-calendar-day${day.key === toLocalDateKey(new Date()) ? ' cleaner-calendar-day-today' : ''}${dayJobs.length ? '' : ' cleaner-calendar-day-empty'}`} key={day.key}>
                                    <span className="cleaner-calendar-day-number">{day.date.getDate() === 1 ? `${MONTH_NAMES[day.date.getMonth()].slice(0, 3)} ` : ''}{day.date.getDate()}</span>
                                    <div className="cleaner-calendar-day-jobs">
                                        {dayJobs.map((job) => <article className={`cleaner-calendar-job cleaner-calendar-job-${job.isOfferToMe ? 'offered' : job.status}`} key={job.id}>
                                            <strong>{job.property?.name || 'Property'}</strong>
                                            <span>{job.isOfferToMe ? 'Offer' : job.status} · {job.property?.bedrooms || 0} bd / {job.property?.bathrooms || 0} ba</span>
                                            <span className="cleaner-calendar-job-rate">{money(job.agreedRate ?? job.suggestedRate)}</span>
                                            {job.isOfferToMe && <div className="cleaner-calendar-job-actions"><button type="button" onClick={() => respondToOffer(job, 'accept')} disabled={saving === `respond-${job.id}`}>Accept</button><button type="button" onClick={() => respondToOffer(job, 'decline')} disabled={saving === `respond-${job.id}`}>Decline</button></div>}
                                            {job.status === 'open' && !job.isOfferToMe && (job.canClaim || job.canRequest) && <button type="button" className="cleaner-calendar-job-action" onClick={() => takeJob(job)} disabled={saving === `job-${job.id}`}>{job.hasRequested ? 'Request pending' : job.canClaim ? 'Claim' : 'Request'}</button>}
                                            {job.status === 'accepted' && job.isAssignedToMe && <button type="button" className="cleaner-calendar-job-action" onClick={() => completeJob(job)} disabled={saving === `complete-${job.id}`}>Complete</button>}
                                        </article>)}
                                    </div>
                                    {!dayJobs.length && <span className="cleaner-calendar-open-day">No cleanings</span>}
                                </div>;
                            })}
                        </div>
                    )}
                </section>
            )}

            {tab === 'availability' && (
                <section className="cleaner-availability-view">
                    <div className="cleaner-dashboard-section-heading"><div><h2>Weekly availability</h2><p>Unavailable weekdays are hidden from new cleaning offers.</p></div><button type="button" className="cleaner-button cleaner-button-primary" disabled={saving === 'availability'} onClick={saveAvailability}>Save days</button></div>
                    <div className="cleaner-weekday-grid">{WEEKDAYS.map((name, dayOfWeek) => {
                        const row = days.find((entry) => Number(entry.dayOfWeek) === dayOfWeek);
                        return <div className="cleaner-weekday-toggle" key={name}>
                            <label className="cleaner-weekday-main"><input type="checkbox" checked={row?.isAvailable ?? true} onChange={(event) => setDays((current) => current.map((entry) => Number(entry.dayOfWeek) === dayOfWeek ? { ...entry, isAvailable: event.target.checked } : entry))} /><span>{name}</span><em>{row?.isAvailable ?? true ? 'Available' : 'Off'}</em></label>
                            <DatePicker ariaLabel={`${name} start time`} selected={timeToDate(row?.startTime || '08:00')} onChange={(date) => date && setDays((current) => current.map((entry) => Number(entry.dayOfWeek) === dayOfWeek ? { ...entry, startTime: dateToTime(date) } : entry))} showTimeSelect showTimeSelectOnly timeIntervals={15} timeCaption="Start" dateFormat="h:mm aa" className="cleaner-time-picker-input" disabled={!row?.isAvailable} />
                            <span className="cleaner-hours-separator">to</span>
                            <DatePicker ariaLabel={`${name} end time`} selected={timeToDate(row?.endTime || '18:00')} onChange={(date) => date && setDays((current) => current.map((entry) => Number(entry.dayOfWeek) === dayOfWeek ? { ...entry, endTime: dateToTime(date) } : entry))} showTimeSelect showTimeSelectOnly timeIntervals={15} timeCaption="End" dateFormat="h:mm aa" className="cleaner-time-picker-input" disabled={!row?.isAvailable} />
                        </div>;
                    })}</div>
                    <div className="cleaner-blocked-dates"><h2>Blocked dates</h2><p>Block a full date when you cannot take a cleaning.</p>
                        <form className="cleaner-block-form" onSubmit={addBlock}><label>Date<input required type="date" value={blockDate} onChange={(event) => setBlockDate(event.target.value)} /></label><label>Note (optional)<input value={blockNote} onChange={(event) => setBlockNote(event.target.value)} placeholder="Unavailable" /></label><button className="cleaner-button cleaner-button-primary" disabled={saving === 'block'}>Block date</button></form>
                        {blocks.length ? <ul>{blocks.map((block) => <li key={block.id}><span>{dateLabel(block.blockedDate)}{block.note ? ` · ${block.note}` : ''}</span><button type="button" className="cleaner-link-button" disabled={saving === `block-${block.id}`} onClick={() => removeBlock(block)}>Remove</button></li>)}</ul> : <p className="cleaner-dashboard-empty">No dates blocked.</p>}
                    </div>
                </section>
            )}

            {tab === 'alerts' && (
                <section className="cleaner-alerts-view">
                    {loading ? <p className="cleaner-dashboard-empty">Loading alerts...</p> : notifications.length === 0 ? <p className="cleaner-dashboard-empty">No alerts yet.</p> : notifications.map((notification) => {
                        const pendingOffer = notification.type === 'job_offer'
                            ? jobs.find((job) => Number(job.id) === Number(notification.jobId) && job.isOfferToMe)
                            : null;
                        return <article key={notification.id} className={`cleaner-alert-item${notification.readAt ? '' : ' cleaner-alert-unread'}`}>
                            <span className="cleaner-alert-unread-dot" aria-hidden="true" />
                            <div className="cleaner-alert-content">
                                <button type="button" className="cleaner-alert-read-button" onClick={() => markRead(notification)}>
                                    <strong>{notification.title}</strong><span>{notification.body}</span><time>{new Date(notification.createdAt).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time>
                                </button>
                                {pendingOffer && <div className="cleaner-alert-offer-actions">
                                    <span>{pendingOffer.property?.name || 'Cleaning'} · {dateLabel(pendingOffer.checkoutDate)}</span>
                                    <div>
                                        <button type="button" className="cleaner-button cleaner-button-primary" disabled={saving === `respond-${pendingOffer.id}`} onClick={() => respondToOffer(pendingOffer, 'accept', notification)}>Accept offer</button>
                                        <button type="button" className="cleaner-button cleaner-button-secondary" disabled={saving === `respond-${pendingOffer.id}`} onClick={() => respondToOffer(pendingOffer, 'decline', notification)}>Decline offer</button>
                                    </div>
                                </div>}
                            </div>
                        </article>;
                    })}
                </section>
            )}
        </main>
    );
};

export default CleanerDashboard;
